// File: src/pages/ProfileEditPage.jsx
// Description: Modern, mobile-friendly profile edit page with enhanced UX and visual appeal.

import React, { useState, useContext, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Avatar, 
  Card, 
  CardContent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  ContactPhone as ContactPhoneIcon,
  Share as ShareIcon,
  Code as CodeIcon,
  ShoppingCart as ShoppingCartIcon,
  Article as ArticleIcon,
  Videocam as VideocamIcon,
  Palette as PaletteIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config';

const ProfileEditPage = () => {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Form steps
  const steps = ['Basic Info', 'Contact & Work', 'Social Media', 'Review & Save'];
  const [activeStep, setActiveStep] = useState(0);

  // Initialize formData with user data
  const [formData, setFormData] = useState({
    // Basic Information
    name: user?.name || '',
    profile_picture: user?.profile_picture || '',
    job_title: user?.job_title || '',
    company: user?.company || '',
    company_logo: user?.company_logo || '',
    location: user?.location || '',
    
    // Contact Information
    phone_number: user?.phone_number || '',
    email: user?.email || '',
    website: user?.website || '',
    business_address: user?.business_address || '',
    
    // About Section
    bio: user?.bio || '',
    interests: user?.interests || '',
    
    // Work & Experience
    current_job_role: user?.current_job_role || '',
    past_work_experience: user?.past_work_experience || '',
    achievements: user?.achievements || '',
    projects: user?.projects || '',

    // Social Media Platforms
    facebook: user?.facebook || '',
    instagram: user?.instagram || '',
    twitter: user?.twitter || '',
    linkedin: user?.linkedin || '',
    youtube: user?.youtube || '',
    whatsapp: user?.whatsapp || '',
    tiktok: user?.tiktok || '',
    github: user?.github || '',
    stack_overflow: user?.stack_overflow || '',
    crunchbase: user?.crunchbase || '',
    glassdoor: user?.glassdoor || '',
    researchgate: user?.researchgate || '',
    academia: user?.academia || '',
    google_scholar: user?.google_scholar || '',
    pinterest: user?.pinterest || '',
    behance: user?.behance || '',
    dribbble: user?.dribbble || '',
    twitch: user?.twitch || '',
    reddit: user?.reddit || '',
    discord: user?.discord || '',
    tumblr: user?.tumblr || '',
    amazon: user?.amazon || '',
    ebay: user?.ebay || '',
    etsy: user?.etsy || '',
    shopify: user?.shopify || '',
    medium: user?.medium || '',
    wordpress: user?.wordpress || '',
    substack: user?.substack || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef(null);

  // Compress image client-side before saving to backend
  const compressImage = (file, maxWidth = 512, maxHeight = 512, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = () => {
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let { width, height } = img;

            // Maintain aspect ratio within bounds
            if (width > height) {
              if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve(dataUrl);
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      } catch (e) {
        reject(e);
      }
    });
  };

  const handleProfilePictureSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setIsCompressing(true);
    try {
      const dataUrl = await compressImage(file);
      handleChange('profile_picture', dataUrl);
    } catch (err) {
      console.error('Image compression failed:', err);
      setError('Failed to process image. Please try a different file.');
    } finally {
      setIsCompressing(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  // Handle previous step
  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Submitting profile update to:', `${BASE_URL}/users/me`);
      console.log('Form data:', formData);

      const response = await fetch(`${BASE_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      console.log('Profile updated successfully:', updatedUser);

      // Update context with new user data
      login({
        ...user,
        ...updatedUser,
      });

      setSuccess(true);
      setTimeout(() => {
        console.log('Navigating to dashboard...');
      navigate('/dashboard');
      }, 2000);

    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Social media platform groups
  const socialPlatforms = [
    {
      title: 'Social Media & Communication',
      icon: <PeopleIcon />,
      platforms: [
        { key: 'facebook', label: 'Facebook', color: '#4267B2' },
        { key: 'instagram', label: 'Instagram', color: '#C13584' },
        { key: 'twitter', label: 'Twitter (X)', color: '#1DA1F2' },
        { key: 'linkedin', label: 'LinkedIn', color: '#0e76a8' },
        { key: 'youtube', label: 'YouTube', color: '#FF0000' },
        { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
        { key: 'tiktok', label: 'TikTok', color: '#010101' },
      ]
    },
    {
      title: 'Professional & Tech',
      icon: <CodeIcon />,
      platforms: [
        { key: 'github', label: 'GitHub', color: '#333' },
        { key: 'stack_overflow', label: 'Stack Overflow', color: '#F48024' },
        { key: 'crunchbase', label: 'Crunchbase', color: '#003366' },
        { key: 'glassdoor', label: 'Glassdoor', color: '#0B84A5' },
        { key: 'researchgate', label: 'ResearchGate', color: '#009999' },
        { key: 'academia', label: 'Academia.edu', color: '#6A1B9A' },
        { key: 'google_scholar', label: 'Google Scholar', color: '#4285F4' },
      ]
    },
    {
      title: 'Creative & Visual',
      icon: <PaletteIcon />,
      platforms: [
        { key: 'pinterest', label: 'Pinterest', color: '#E60023' },
        { key: 'behance', label: 'Behance', color: '#1769FF' },
        { key: 'dribbble', label: 'Dribbble', color: '#EA4C89' },
        { key: 'twitch', label: 'Twitch', color: '#6441A5' },
        { key: 'reddit', label: 'Reddit', color: '#FF4500' },
        { key: 'discord', label: 'Discord', color: '#7289da' },
        { key: 'tumblr', label: 'Tumblr', color: '#34526f' },
      ]
    },
    {
      title: 'E-commerce & Publishing',
      icon: <ShoppingCartIcon />,
      platforms: [
        { key: 'amazon', label: 'Amazon', color: '#FF9900' },
        { key: 'ebay', label: 'eBay', color: '#E53238' },
        { key: 'etsy', label: 'Etsy', color: '#D5641C' },
        { key: 'shopify', label: 'Shopify', color: '#96BF48' },
        { key: 'medium', label: 'Medium', color: '#000' },
        { key: 'wordpress', label: 'WordPress', color: '#21759B' },
        { key: 'substack', label: 'Substack', color: '#000' },
      ]
    }
  ];

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Basic Info
  return (
          <Grid container spacing={3}>
            {/* Profile Picture */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={formData.profile_picture}
                  sx={{ 
                    width: 120, 
                    height: 120,
                    border: `4px solid ${theme.palette.primary[100]}`,
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {formData.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': { backgroundColor: theme.palette.primary.dark }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Upload profile picture"
                  disabled={isCompressing}
                >
                  <PhotoCameraIcon />
                </IconButton>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleProfilePictureSelect}
                  style={{ display: 'none' }}
                />
              </Box>
            </Grid>

            {/* Basic Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.job_title}
                onChange={(e) => handleChange('job_title', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                placeholder="Tell us about yourself..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interests & Skills"
                value={formData.interests}
                onChange={(e) => handleChange('interests', e.target.value)}
                variant="outlined"
                placeholder="e.g., React, Node.js, AI/ML, Photography"
                helperText="Separate multiple interests with commas"
              />
            </Grid>
          </Grid>
        );

      case 1: // Contact & Work
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                variant="outlined"
                helperText="Email cannot be changed"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                variant="outlined"
                placeholder="https://yourwebsite.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Address"
                value={formData.business_address}
                onChange={(e) => handleChange('business_address', e.target.value)}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Work & Experience
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Job Role"
                value={formData.current_job_role}
                onChange={(e) => handleChange('current_job_role', e.target.value)}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Past Work Experience"
                value={formData.past_work_experience}
                onChange={(e) => handleChange('past_work_experience', e.target.value)}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Achievements"
                value={formData.achievements}
                onChange={(e) => handleChange('achievements', e.target.value)}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Projects"
                value={formData.projects}
                onChange={(e) => handleChange('projects', e.target.value)}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        );

      case 2: // Social Media
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Social Media & Online Presence
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Connect your social media profiles to make it easier for people to find and connect with you.
            </Typography>

            {socialPlatforms.map((group, groupIndex) => (
              <Card key={groupIndex} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1, 
                      backgroundColor: theme.palette.primary[100],
                      mr: 2
                    }}>
                      {group.icon}
              </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {group.title}
                  </Typography>
                </Box>

                  <Grid container spacing={2}>
                    {group.platforms.map((platform) => (
                      <Grid item xs={12} sm={6} key={platform.key}>
              <TextField
                          fullWidth
                          label={platform.label}
                          value={formData[platform.key] || ''}
                          onChange={(e) => handleChange(platform.key, e.target.value)}
                          variant="outlined"
                          size="small"
                InputProps={{
                            startAdornment: (
                              <Box sx={{ 
                                width: 16, 
                                height: 16, 
                                borderRadius: '50%', 
                                backgroundColor: platform.color,
                                mr: 1
                              }} />
                  ),
                }}
              />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 3: // Review & Save
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Review Your Profile
            </Typography>

            {/* Profile Summary */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={formData.profile_picture}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  >
                    {formData.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formData.name || 'Your Name'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.job_title || 'Job Title'} {formData.company && `@ ${formData.company}`}
                    </Typography>
                    {formData.location && (
                      <Typography variant="body2" color="text.secondary">
                        📍 {formData.location}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {formData.bio && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formData.bio}
                  </Typography>
                )}

                {formData.interests && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.interests.split(',').map((interest, index) => (
                      <Chip
                        key={index}
                        label={interest.trim()}
                        size="small"
                        sx={{ backgroundColor: theme.palette.primary[100] }}
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Connected Platforms Count */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Connected Platforms
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: theme.palette.primary.main,
                  textAlign: 'center'
                }}>
                  {Object.values(formData).filter(value => value && typeof value === 'string' && value.trim() !== '').length - 4}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  social media platforms connected
                </Typography>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Edit Profile
          </Typography>
          </Box>

        <Typography variant="body1" color="text.secondary">
          Update your information and connect your social media profiles
            </Typography>
      </Box>

      {/* Progress Stepper */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              '& .MuiStepLabel-root .Mui-completed': {
                color: theme.palette.success.main,
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: theme.palette.primary.main,
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Mobile Step Indicator */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {steps[activeStep]}
                    </Typography>
                </Box>
        </CardContent>
      </Card>

      {/* Error/Success Alerts */}
          {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Profile updated successfully! Redirecting to dashboard...
        </Alert>
      )}

      {/* Step Content */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          {renderStepContent(activeStep)}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ 
            borderRadius: 2,
            px: 4,
            py: 1.5
          }}
        >
          Back
        </Button>

        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="gradient"
              onClick={handleSubmit}
              disabled={loading || isCompressing}
              startIcon={(loading || isCompressing) ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ 
                borderRadius: 2,
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              {loading || isCompressing ? 'Please wait...' : 'Save Profile'}
            </Button>
          ) : (
            <Button
              variant="gradient"
              onClick={handleNext}
              sx={{ 
                borderRadius: 2,
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Next
          </Button>
          )}
        </Box>
        </Box>
      </Container>
  );
};

export default ProfileEditPage;
